/**
 * MintPage
 *
 * Single-page UX for a creator to mint one NFT directly from their wallet.
 * Picks an existing collection or creates a new one (optionally under a project) inline,
 * then builds an NFTokenMint transaction which the creator signs via Xaman.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FeatherArrowLeft,
  FeatherCheck,
  FeatherImage,
  FeatherPlus,
  FeatherTrash,
  FeatherUpload,
  FeatherX,
} from '@subframe/core';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { Button } from '../ui/components/Button';
import { Checkbox } from '../ui/components/Checkbox';
import { IconButton } from '../ui/components/IconButton';
import { Select } from '../ui/components/Select';
import { TextArea } from '../ui/components/TextArea';
import { TextField } from '../ui/components/TextField';
import { Alert } from '../ui/components/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { WalletSigningModal } from '../components/transactions/WalletSigningModal';
import { toSigningPayload } from '../lib/wallet';
import type { SigningPayload } from '../types/signing';
import { useAuthStore } from '../store/authStore';
import {
  getMyProjects,
  getMyCollections,
  createProject,
  type ManagedProject,
  type CreatorCollection,
} from '../api/creator';
import {
  checkTaxonAvailable,
  getMintStatus,
  prepareMint,
  uploadMintImages,
  type PinnedImage,
} from '../api/mint';

type CollectionMode = 'existing' | 'new';
type ProjectChoice = 'none' | 'existing' | 'new';

interface TraitRow {
  id: string;
  trait_type: string;
  value: string;
}

function newTraitId() {
  return Math.random().toString(36).slice(2, 10);
}

function slugify(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24);
}

// Accept strings for the rich/extra media inputs. `.glb` is included explicitly
// because browsers often report an empty/octet-stream mime for 3D models.
const RICH_ACCEPT = 'video/mp4,video/webm,.glb,model/gltf-binary';
const EXTRA_ACCEPT = `image/*,${RICH_ACCEPT}`;

function isImageFile(f: File): boolean {
  return f.type.startsWith('image/');
}

function fileExtLabel(f: File): string {
  return (f.name.split('.').pop() || 'file').toUpperCase();
}

export const MintPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  // Collection selection
  const [collectionMode, setCollectionMode] = useState<CollectionMode>('existing');
  const [myCollections, setMyCollections] = useState<CreatorCollection[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(true);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('');

  // New collection form
  const [newColName, setNewColName] = useState('');
  const [newColSlug, setNewColSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [newColDescription, setNewColDescription] = useState('');
  const [newColTaxon, setNewColTaxon] = useState<string>('');
  const [taxonChecking, setTaxonChecking] = useState(false);
  const [taxonAvailable, setTaxonAvailable] = useState<boolean | null>(null);
  const [taxonError, setTaxonError] = useState<string | null>(null);
  const [newColRoyalty, setNewColRoyalty] = useState<string>('0');
  const [newColImage, setNewColImage] = useState<File | null>(null);
  const [newColBanner, setNewColBanner] = useState<File | null>(null);

  // Project association (only relevant for new collection)
  const [projectChoice, setProjectChoice] = useState<ProjectChoice>('none');
  const [myProjects, setMyProjects] = useState<ManagedProject[]>([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');

  // NFT form
  const [nftName, setNftName] = useState('');
  const [nftDescription, setNftDescription] = useState('');
  const [primaryImage, setPrimaryImage] = useState<File | null>(null);
  // The single "main" rich asset (video or 3D model) → animation_url.
  const [animationFile, setAnimationFile] = useState<File | null>(null);
  const [altImages, setAltImages] = useState<File[]>([]);
  const [traits, setTraits] = useState<TraitRow[]>([]);
  const [royaltyOverride, setRoyaltyOverride] = useState<string>('');
  const [flagTransferable, setFlagTransferable] = useState(true);
  const [flagBurnable, setFlagBurnable] = useState(false);
  const [flagOnlyXrp, setFlagOnlyXrp] = useState(false);
  const [flagMutable, setFlagMutable] = useState(false);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [xamanPayload, setXamanPayload] = useState<SigningPayload | null>(null);
  // Post-sign state: we poll /mint/status/:tx until the NFT row is enriched, then redirect.
  const [finalizeTxHash, setFinalizeTxHash] = useState<string | null>(null);
  const [finalizeMessage, setFinalizeMessage] = useState('Finalizing your mint…');

  const primaryFileRef = useRef<HTMLInputElement>(null);
  const animationFileRef = useRef<HTMLInputElement>(null);
  const altFilesRef = useRef<HTMLInputElement>(null);
  const colImageRef = useRef<HTMLInputElement>(null);
  const colBannerRef = useRef<HTMLInputElement>(null);

  const selectedCollection = useMemo(
    () => myCollections.find(c => c.id === selectedCollectionId) || null,
    [myCollections, selectedCollectionId]
  );

  // Load user's collections on mount
  useEffect(() => {
    if (!isAuthenticated) {
      setCollectionsLoading(false);
      return;
    }
    // Fetch ALL the user's collections (including ones in projects), not just orphaned.
    (async () => {
      try {
        const cols = await getMyCollections({ includeProjectLinked: true });
        setMyCollections(cols);
        // If they have no collections, default to "new" mode.
        if (cols.length === 0) {
          setCollectionMode('new');
        }
      } catch (err) {
        console.error('Failed to load collections', err);
      } finally {
        setCollectionsLoading(false);
      }
    })();
  }, [isAuthenticated]);

  // Load projects lazily when creating a new collection
  useEffect(() => {
    if (collectionMode === 'new' && !projectsLoaded && isAuthenticated) {
      getMyProjects()
        .then(projs => {
          setMyProjects(projs);
          setProjectsLoaded(true);
        })
        .catch(err => {
          console.error('Failed to load projects', err);
          setProjectsLoaded(true);
        });
    }
  }, [collectionMode, projectsLoaded, isAuthenticated]);

  // Auto-suggest slug from name
  useEffect(() => {
    if (!slugManuallyEdited && newColName) {
      setNewColSlug(slugify(newColName));
    }
  }, [newColName, slugManuallyEdited]);

  // Debounced taxon availability check
  useEffect(() => {
    setTaxonError(null);
    if (collectionMode !== 'new') return;
    const trimmed = newColTaxon.trim();
    if (!trimmed) {
      setTaxonAvailable(null);
      return;
    }
    const n = Number(trimmed);
    if (!Number.isInteger(n) || n < 0 || n > 0xFFFFFFFF) {
      setTaxonAvailable(null);
      setTaxonError('Taxon must be an integer between 0 and 4294967295');
      return;
    }
    setTaxonChecking(true);
    const handle = setTimeout(async () => {
      try {
        const result = await checkTaxonAvailable(n);
        setTaxonAvailable(result.available);
        if (!result.available && result.existingCollection) {
          setTaxonError(`Taxon already used by your collection "${result.existingCollection.name}"`);
        }
      } catch (err) {
        console.error('Taxon check failed', err);
      } finally {
        setTaxonChecking(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [newColTaxon, collectionMode]);

  // Pre-fill the per-NFT royalty field with the selected collection's default so
  // the creator can see at a glance what they'll pay. Any manual edit survives
  // until they switch collections again.
  useEffect(() => {
    if (collectionMode === 'existing') {
      if (selectedCollection) {
        const pct = Number(selectedCollection.royalty_percentage ?? 0);
        setRoyaltyOverride(Number.isFinite(pct) ? String(pct) : '');
      } else {
        setRoyaltyOverride('');
      }
    } else {
      setRoyaltyOverride('');
    }
  }, [collectionMode, selectedCollection]);

  const canSubmit = useMemo(() => {
    if (!primaryImage) return false;
    if (!nftName.trim()) return false;
    if (collectionMode === 'existing' && !selectedCollectionId) return false;
    if (collectionMode === 'new') {
      if (!newColName.trim()) return false;
      if (!newColTaxon.trim()) return false;
      if (taxonAvailable !== true) return false;
      const royalty = Number(newColRoyalty);
      if (!Number.isFinite(royalty) || royalty < 0 || royalty > 50) return false;
      if (projectChoice === 'existing' && !selectedProjectId) return false;
      if (projectChoice === 'new' && !newProjectName.trim()) return false;
    }
    if (royaltyOverride.trim() !== '') {
      const o = Number(royaltyOverride);
      if (!Number.isFinite(o) || o < 0 || o > 50) return false;
    }
    return true;
  }, [
    primaryImage,
    nftName,
    collectionMode,
    selectedCollectionId,
    newColName,
    newColTaxon,
    taxonAvailable,
    newColRoyalty,
    projectChoice,
    selectedProjectId,
    newProjectName,
    royaltyOverride,
  ]);

  const addTrait = () => setTraits(prev => [...prev, { id: newTraitId(), trait_type: '', value: '' }]);
  const updateTrait = (id: string, field: 'trait_type' | 'value', val: string) =>
    setTraits(prev => prev.map(t => (t.id === id ? { ...t, [field]: val } : t)));
  const removeTrait = (id: string) => setTraits(prev => prev.filter(t => t.id !== id));

  const handleAltFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files || []);
    setAltImages(prev => [...prev, ...list].slice(0, 10));
    if (altFilesRef.current) altFilesRef.current.value = '';
  };

  const removeAltImage = (idx: number) =>
    setAltImages(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!canSubmit || !primaryImage) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      // 1. Create project first if requested (needs an ID before we call /mint/prepare).
      let projectIdForCollection: string | null = null;
      if (collectionMode === 'new' && projectChoice === 'existing') {
        projectIdForCollection = selectedProjectId || null;
      } else if (collectionMode === 'new' && projectChoice === 'new') {
        const created = await createProject({
          name: newProjectName.trim(),
          description: newProjectDescription.trim() || undefined,
        });
        projectIdForCollection = created.id;
      }

      // 2. Upload all files to IPFS in a single batch. Order matters — we slice the
      //    returned pins back out by the same order we pushed them in:
      //    [primary, animation?, ...alt, colImage?, colBanner?]
      const filesToPin: File[] = [primaryImage];
      if (animationFile) filesToPin.push(animationFile);
      filesToPin.push(...altImages);
      if (collectionMode === 'new') {
        if (newColImage) filesToPin.push(newColImage);
        if (newColBanner) filesToPin.push(newColBanner);
      }
      const pinned = await uploadMintImages(filesToPin);

      let cursor = 0;
      const primaryPin = pinned[cursor++];
      const animationPin: PinnedImage | undefined = animationFile ? pinned[cursor++] : undefined;
      const altPins = pinned.slice(cursor, cursor + altImages.length);
      cursor += altImages.length;
      let colImagePin: PinnedImage | undefined;
      let colBannerPin: PinnedImage | undefined;
      if (collectionMode === 'new') {
        if (newColImage) { colImagePin = pinned[cursor++]; }
        if (newColBanner) { colBannerPin = pinned[cursor++]; }
      }

      // 3. Build prepare payload.
      const collectionPayload =
        collectionMode === 'existing'
          ? { id: selectedCollectionId }
          : {
              newCollection: {
                name: newColName.trim(),
                description: newColDescription.trim() || undefined,
                slug: newColSlug || undefined,
                taxon: Number(newColTaxon),
                royalty_percentage: Number(newColRoyalty),
                image_cid: colImagePin?.cid,
                banner_cid: colBannerPin?.cid,
                project_id: projectIdForCollection,
              },
            };

      const result = await prepareMint({
        collection: collectionPayload as any,
        nft: {
          name: nftName.trim(),
          description: nftDescription.trim() || undefined,
          primary_image: { cid: primaryPin.cid, mime_type: primaryPin.mime_type },
          animation: animationPin ? { cid: animationPin.cid, mime_type: animationPin.mime_type } : undefined,
          alternative_images: altPins.map(p => ({ cid: p.cid, mime_type: p.mime_type })),
          traits: traits
            .filter(t => t.trait_type.trim() && t.value.trim())
            .map(t => ({ trait_type: t.trait_type.trim(), value: t.value.trim() })),
        },
        transfer_fee_override: royaltyOverride.trim() !== '' ? Number(royaltyOverride) : undefined,
        flags: {
          transferable: flagTransferable,
          burnable: flagBurnable,
          only_xrp: flagOnlyXrp,
          mutable: flagMutable,
        },
      });

      setXamanPayload(toSigningPayload(result.signingIntent ?? result.payload));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to prepare mint';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const onSignSuccess = (txid: string) => {
    setXamanPayload(null);
    setFinalizeTxHash(txid);
  };

  // Poll /mint/status for up to MAX_POLL_MS. Navigate as soon as metadata is enriched,
  // OR at the deadline — the NFT page handles partial-enrichment states on its own.
  useEffect(() => {
    if (!finalizeTxHash) return;
    const MAX_POLL_MS = 20_000;
    const INTERVAL_MS = 1_500;
    const start = Date.now();
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const status = await getMintStatus(finalizeTxHash);
        if (cancelled) return;

        // Update the user-visible message as state progresses.
        if (!status.validated) {
          setFinalizeMessage('Waiting for the ledger to validate the mint…');
        } else if (!status.indexed) {
          setFinalizeMessage('Confirmed on the ledger. Indexing the new NFT…');
        } else if (!status.enriched) {
          setFinalizeMessage('Pinning metadata and media to IPFS…');
        }

        if (status.tokenId && (status.enriched || Date.now() - start >= MAX_POLL_MS)) {
          navigate(`/nfts/${status.tokenId}`);
          return;
        }
      } catch (err) {
        // Transient errors (XRPL tx not yet validated) — keep polling.
        console.warn('mint status poll failed', err);
      }
      if (Date.now() - start >= MAX_POLL_MS) {
        // Didn't even get a tokenId in 20s. Leave the spinner but stop polling.
        setFinalizeMessage('Your mint was signed but is taking longer than usual to appear. It should show up in your wallet shortly.');
        return;
      }
      setTimeout(poll, INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [finalizeTxHash, navigate]);

  if (finalizeTxHash) {
    return (
      <DefaultPageLayout>
        <div className="container max-w-2xl mx-auto px-4 py-24 flex flex-col items-center text-center gap-6">
          <LoadingSpinner size="xl" className="text-brand-600" />
          <h1 className="text-heading-2 font-heading-2 text-default-font">Finalizing your mint</h1>
          <p className="text-body font-body text-subtext-color max-w-md">{finalizeMessage}</p>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="container max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link to="/launchpad" className="inline-flex items-center gap-1 text-caption font-caption text-subtext-color hover:text-default-font">
            <FeatherArrowLeft className="w-4 h-4" /> Back
          </Link>
          <h1 className="text-heading-1 font-heading-1 text-default-font mt-2">Mint NFT</h1>
          <p className="text-body font-body text-subtext-color mt-1">
            Mint a single NFT directly from your wallet. You'll sign the mint transaction in Xaman — the NFT is yours immediately.
          </p>
        </div>

        {submitError && (
          <Alert
            variant="error"
            title="Mint failed"
            description={submitError}
            className="mb-6"
          />
        )}

        {/* Section 1: Collection */}
        <section className="rounded-xl border border-neutral-border bg-default-background p-5 mb-6">
          <h2 className="text-heading-3 font-heading-3 text-default-font mb-4">Collection</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <ModeCard
              selected={collectionMode === 'existing'}
              onClick={() => setCollectionMode('existing')}
              title="Mint into an existing collection"
              description="Choose one of your existing collections."
              disabled={!collectionsLoading && myCollections.length === 0}
              disabledReason="You don't have any collections yet."
            />
            <ModeCard
              selected={collectionMode === 'new'}
              onClick={() => setCollectionMode('new')}
              title="Create a new collection"
              description="Give the collection a name, taxon, and default royalty. You can mint more NFTs into it later."
            />
          </div>

          {collectionMode === 'existing' && (
            <div>
              {collectionsLoading ? (
                <div className="py-6 flex items-center justify-center">
                  <LoadingSpinner size="sm" />
                </div>
              ) : myCollections.length === 0 ? (
                <p className="text-body font-body text-subtext-color">
                  You don't have any collections yet. Switch to "Create a new collection" above.
                </p>
              ) : (
                <Select
                  value={selectedCollectionId || ''}
                  onValueChange={val => setSelectedCollectionId(val || '')}
                  placeholder="Select a collection..."
                  className="w-full"
                >
                  {myCollections.map(c => (
                    <Select.Item key={c.id} value={c.id}>
                      {c.name} · taxon {c.taxon}
                    </Select.Item>
                  ))}
                </Select>
              )}
            </div>
          )}

          {collectionMode === 'new' && (
            <div className="flex flex-col gap-4">
              <TextField
                label={<span>Collection name <span className="text-error-600">*</span></span>}
              >
                <TextField.Input
                  value={newColName}
                  onChange={e => setNewColName(e.target.value)}
                  placeholder="My Collection"
                  maxLength={255}
                />
              </TextField>

              <TextField
                label="Slug"
                helpText="Used in URLs. Auto-generated from the name."
              >
                <TextField.Input
                  value={newColSlug}
                  onChange={e => {
                    setNewColSlug(e.target.value);
                    setSlugManuallyEdited(true);
                  }}
                  placeholder="my-collection"
                  maxLength={24}
                />
              </TextField>

              <TextArea label="Description (optional)">
                <TextArea.Input
                  value={newColDescription}
                  onChange={e => setNewColDescription(e.target.value)}
                  placeholder="A short description of your collection"
                  maxLength={5000}
                />
              </TextArea>

              <div className="grid grid-cols-2 gap-4">
                <FileUploadField
                  label="Thumbnail (optional)"
                  accept="image/*"
                  file={newColImage}
                  onChange={setNewColImage}
                  inputRef={colImageRef}
                />
                <FileUploadField
                  label="Banner (optional)"
                  accept="image/*"
                  file={newColBanner}
                  onChange={setNewColBanner}
                  inputRef={colBannerRef}
                />
              </div>

              <TextField
                label={<span>Taxon <span className="text-error-600">*</span></span>}
                helpText={
                  taxonChecking ? (
                    'Checking availability…'
                  ) : taxonAvailable === true ? (
                    <span className="flex items-center gap-1 text-success-600">
                      <FeatherCheck className="w-3 h-3" /> Available
                    </span>
                  ) : taxonError ? (
                    <span className="text-error-600">{taxonError}</span>
                  ) : (
                    'A number that groups your NFTs into this collection. Must be unique for your wallet.'
                  )
                }
                error={!!taxonError}
              >
                <TextField.Input
                  type="text"
                  inputMode="numeric"
                  value={newColTaxon}
                  onChange={e => setNewColTaxon(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="e.g. 1"
                />
              </TextField>

              <TextField
                label={<span>Default royalty % <span className="text-error-600">*</span></span>}
                helpText="Applied to every NFT minted into this collection. Max 50%. Can be overridden per NFT."
              >
                <TextField.Input
                  type="text"
                  inputMode="decimal"
                  value={newColRoyalty}
                  onChange={e => setNewColRoyalty(e.target.value.replace(/[^0-9.]/g, ''))}
                  placeholder="0"
                />
              </TextField>

              {/* Project association */}
              <div className="pt-4 border-t border-neutral-border">
                <div className="text-body-bold font-body-bold text-default-font mb-2">Project</div>
                <p className="text-caption font-caption text-subtext-color mb-3">
                  Collections can belong to a project for shared branding. This is optional.
                </p>

                <div className="grid gap-2">
                  <ModeCard
                    selected={projectChoice === 'none'}
                    onClick={() => setProjectChoice('none')}
                    title="No project"
                    description="This collection stands on its own."
                  />
                  <ModeCard
                    selected={projectChoice === 'existing'}
                    onClick={() => setProjectChoice('existing')}
                    title="Add to existing project"
                    description="Use one of the projects you already manage."
                  />
                  {projectChoice === 'existing' && (
                    <div className="ml-1" onClick={e => e.stopPropagation()}>
                      {myProjects.length > 0 ? (
                        <Select
                          value={selectedProjectId}
                          onValueChange={val => setSelectedProjectId(val)}
                          placeholder="Select a project…"
                          className="w-full"
                        >
                          {myProjects.map(p => (
                            <Select.Item key={p.id} value={p.id}>{p.name}</Select.Item>
                          ))}
                        </Select>
                      ) : (
                        <p className="text-caption font-caption text-subtext-color">
                          {projectsLoaded ? "You don't have any projects yet." : 'Loading projects…'}
                        </p>
                      )}
                    </div>
                  )}
                  <ModeCard
                    selected={projectChoice === 'new'}
                    onClick={() => setProjectChoice('new')}
                    title="Create new project"
                    description="Start a new project for this collection."
                  />
                  {projectChoice === 'new' && (
                    <div className="ml-1 flex flex-col gap-3" onClick={e => e.stopPropagation()}>
                      <TextField label={<span>Project name <span className="text-error-600">*</span></span>}>
                        <TextField.Input
                          value={newProjectName}
                          onChange={e => setNewProjectName(e.target.value)}
                          placeholder="My Project"
                          maxLength={255}
                        />
                      </TextField>
                      <TextArea label="Project description (optional)">
                        <TextArea.Input
                          value={newProjectDescription}
                          onChange={e => setNewProjectDescription(e.target.value)}
                          placeholder="A brief description"
                          maxLength={5000}
                        />
                      </TextArea>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Section 2: NFT details */}
        <section className="rounded-xl border border-neutral-border bg-default-background p-5 mb-6">
          <h2 className="text-heading-3 font-heading-3 text-default-font mb-4">NFT</h2>

          <div className="flex flex-col gap-4">
            <FileUploadField
              label={<span>Primary image <span className="text-error-600">*</span></span>}
              accept="image/*"
              file={primaryImage}
              onChange={setPrimaryImage}
              inputRef={primaryFileRef}
            />

            <FileUploadField
              label="Animated / 3D asset (optional)"
              helpText="One video (MP4) or 3D model (GLB). This becomes the main asset shown on other marketplaces like xrp.cafe — your primary image stays the preview/thumbnail."
              accept={RICH_ACCEPT}
              file={animationFile}
              onChange={setAnimationFile}
              inputRef={animationFileRef}
            />

            <div>
              <div className="text-body-bold font-body-bold text-default-font mb-1">Additional files (optional)</div>
              <p className="text-caption font-caption text-subtext-color mb-2">
                Up to 10 extra images, videos, or 3D models. Shown in the carousel on xrp.deals.
              </p>
              <input
                ref={altFilesRef}
                type="file"
                accept={EXTRA_ACCEPT}
                multiple
                className="hidden"
                onChange={handleAltFilesChange}
              />
              <Button
                variant="neutral-secondary"
                icon={<FeatherUpload />}
                onClick={() => altFilesRef.current?.click()}
                disabled={altImages.length >= 10}
              >
                {altImages.length === 0 ? 'Add additional files' : `Add more (${altImages.length}/10)`}
              </Button>
              {altImages.length > 0 && (
                <div className="mt-3 grid grid-cols-5 gap-2">
                  {altImages.map((f, idx) => (
                    <div key={`${f.name}-${idx}`} className="relative aspect-square rounded-md overflow-hidden border border-neutral-border">
                      {isImageFile(f) ? (
                        <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-neutral-100 px-1 text-center">
                          <span className="text-caption-bold font-caption-bold text-subtext-color">{fileExtLabel(f)}</span>
                          <span className="text-[10px] leading-tight text-subtext-color truncate w-full">{f.name}</span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeAltImage(idx)}
                        className="absolute top-1 right-1 rounded-full bg-default-background/90 w-6 h-6 flex items-center justify-center hover:bg-default-background"
                        aria-label={`Remove ${f.name}`}
                      >
                        <FeatherX className="w-3.5 h-3.5 text-default-font" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <TextField label={<span>Name <span className="text-error-600">*</span></span>}>
              <TextField.Input
                value={nftName}
                onChange={e => setNftName(e.target.value)}
                placeholder="My NFT #1"
                maxLength={255}
              />
            </TextField>

            <TextArea label="Description (optional)">
              <TextArea.Input
                value={nftDescription}
                onChange={e => setNftDescription(e.target.value)}
                placeholder="Tell people about this NFT"
                maxLength={5000}
              />
            </TextArea>

            {/* Traits */}
            <div>
              <div className="text-body-bold font-body-bold text-default-font mb-2">Traits (optional)</div>
              {traits.length > 0 && (
                <div className="flex flex-col gap-2 mb-2">
                  {traits.map(t => (
                    <div key={t.id} className="flex items-start gap-2">
                      <div className="flex-1">
                        <TextField>
                          <TextField.Input
                            value={t.trait_type}
                            onChange={e => updateTrait(t.id, 'trait_type', e.target.value)}
                            placeholder="Trait (e.g. Background)"
                            maxLength={100}
                          />
                        </TextField>
                      </div>
                      <div className="flex-1">
                        <TextField>
                          <TextField.Input
                            value={t.value}
                            onChange={e => updateTrait(t.id, 'value', e.target.value)}
                            placeholder="Value (e.g. Blue)"
                            maxLength={500}
                          />
                        </TextField>
                      </div>
                      <IconButton
                        icon={<FeatherTrash />}
                        onClick={() => removeTrait(t.id)}
                        variant="neutral-tertiary"
                        aria-label="Remove trait"
                      />
                    </div>
                  ))}
                </div>
              )}
              <Button variant="neutral-secondary" icon={<FeatherPlus />} onClick={addTrait}>
                Add trait
              </Button>
            </div>

            {/* Royalty override */}
            <TextField
              label="Royalty %"
              helpText={
                collectionMode === 'existing' && selectedCollection
                  ? `Pre-filled from the collection's default. Adjust to set a different royalty for this NFT. Max 50%.`
                  : 'Defaults to the collection royalty you set above. Adjust to override for this NFT. Max 50%.'
              }
            >
              <TextField.Input
                type="text"
                inputMode="decimal"
                value={royaltyOverride}
                onChange={e => setRoyaltyOverride(e.target.value.replace(/[^0-9.]/g, ''))}
                placeholder={collectionMode === 'new' ? newColRoyalty || '0' : ''}
              />
            </TextField>

            {/* Flags */}
            <div>
              <div className="text-body-bold font-body-bold text-default-font mb-2">Properties</div>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 text-body font-body text-default-font cursor-pointer">
                  <Checkbox
                    checked={flagTransferable}
                    onCheckedChange={(v: any) => setFlagTransferable(!!v)}
                  />
                  Transferable (can be sold or transferred by the owner)
                </label>
                <label className="flex items-center gap-2 text-body font-body text-default-font cursor-pointer">
                  <Checkbox
                    checked={flagBurnable}
                    onCheckedChange={(v: any) => setFlagBurnable(!!v)}
                  />
                  Burnable by issuer (you can burn this NFT later)
                </label>
                <label className="flex items-center gap-2 text-body font-body text-default-font cursor-pointer">
                  <Checkbox
                    checked={flagOnlyXrp}
                    onCheckedChange={(v: any) => setFlagOnlyXrp(!!v)}
                  />
                  Only XRP (offers for this NFT must be in XRP)
                </label>
                <label className="flex items-center gap-2 text-body font-body text-default-font cursor-pointer">
                  <Checkbox
                    checked={flagMutable}
                    onCheckedChange={(v: any) => setFlagMutable(!!v)}
                  />
                  Mutable (you can update the metadata URI later)
                </label>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <div className="sticky bottom-0 bg-default-background/95 backdrop-blur py-4 border-t border-neutral-border flex items-center justify-end gap-4">
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            loading={submitting}
            icon={<FeatherImage />}
          >
            Sign & Mint
          </Button>
        </div>
      </div>

      {xamanPayload && (
        <WalletSigningModal
          open
          onClose={() => setXamanPayload(null)}
          title="Sign your mint"
          payload={xamanPayload}
          onSuccess={onSignSuccess}
          onRejected={() => setXamanPayload(null)}
          onExpired={() => setXamanPayload(null)}
        />
      )}
    </DefaultPageLayout>
  );
};

// --- Small helpers -----------------------------------------------------------

interface ModeCardProps {
  selected: boolean;
  onClick: () => void;
  title: string;
  description: string;
  disabled?: boolean;
  disabledReason?: string;
}

const ModeCard: React.FC<ModeCardProps> = ({ selected, onClick, title, description, disabled, disabledReason }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    className={`text-left p-4 rounded-lg border-2 transition-colors ${
      selected
        ? 'border-brand-600 bg-brand-50'
        : disabled
        ? 'border-neutral-border opacity-60 cursor-not-allowed'
        : 'border-neutral-border hover:border-neutral-400'
    }`}
  >
    <div className="text-body-bold font-body-bold text-default-font">{title}</div>
    <p className="text-caption font-caption text-subtext-color mt-1">
      {disabled && disabledReason ? disabledReason : description}
    </p>
  </button>
);

interface FileUploadFieldProps {
  label: React.ReactNode;
  accept: string;
  file: File | null;
  onChange: (f: File | null) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  helpText?: string;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({ label, accept, file, onChange, inputRef, helpText }) => {
  // Only images get an object-URL preview; video/3D files show a format chip.
  const previewUrl = useMemo(
    () => (file && file.type.startsWith('image/') ? URL.createObjectURL(file) : null),
    [file]
  );
  return (
    <div>
      <div className="text-body-bold font-body-bold text-default-font mb-1">{label}</div>
      {helpText && (
        <p className="text-caption font-caption text-subtext-color mb-2">{helpText}</p>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={e => onChange(e.target.files?.[0] || null)}
      />
      {file ? (
        <div className="flex items-center gap-3">
          <div className="w-20 h-20 rounded-md overflow-hidden border border-neutral-border flex items-center justify-center bg-neutral-100">
            {previewUrl ? (
              <img src={previewUrl} alt={file.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-caption-bold font-caption-bold text-subtext-color">
                {(file.name.split('.').pop() || 'file').toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-caption font-caption text-default-font truncate">{file.name}</p>
            <p className="text-caption font-caption text-subtext-color">
              {file.size > 1024 * 1024
                ? `${(file.size / 1024 / 1024).toFixed(1)} MB`
                : `${(file.size / 1024).toFixed(1)} KB`}
            </p>
          </div>
          <Button variant="neutral-tertiary" onClick={() => onChange(null)}>Remove</Button>
        </div>
      ) : (
        <Button
          variant="neutral-secondary"
          icon={<FeatherUpload />}
          onClick={() => inputRef.current?.click()}
        >
          Choose file
        </Button>
      )}
    </div>
  );
};

export default MintPage;
